import { memo, useState } from 'react'

import {
  AttachmentType,
  getAttachmentAssetKey,
  useAttachmentTypes,
} from '../../shared/hooks/useAttachmentTypes'
import { useMediaCounter } from '../../shared/hooks/useMediaCounter'
import { useFileDownload } from '@/hooks/useFileDownload'
import { useTranslation } from 'react-i18next'
import { BsEyeFill } from 'react-icons/bs'

import { Box, Flex, IconButton, Image, useDisclosure } from '@chakra-ui/react'

import { FileThumbnail } from '@/components/ui'

import { IncidentNoteVideoPlayer } from '../../add-note/components/IncidentNoteVideoPlayer'
import { MediaPreviewerModal } from '../../feed/components/MediaPreviewerModal'
import { getFileName, getMimeType } from '../../shared/utils/imageHelpers'
import { MediaControls } from './MediaControls'
import { MediaError } from './MediaError'

export interface IncidentImagesIProps {
  images: string[]
}

interface IncidentAttachmentVideoPlayerIProps {
  videoUrl: string
}

const IncidentAttachmentVideoPlayerComponent = ({
  videoUrl,
}: IncidentAttachmentVideoPlayerIProps) => (
  <IncidentNoteVideoPlayer height='324px' videoUrl={videoUrl} />
)

// Polling refreshes the presigned query string every three seconds even though
// the underlying asset is unchanged. Keep the player mounted for the same S3
// path so ReactPlayer does not reload and interrupt playback.
const IncidentAttachmentVideoPlayer = memo(
  IncidentAttachmentVideoPlayerComponent,
  (previous, next) =>
    getAttachmentAssetKey(previous.videoUrl) ===
    getAttachmentAssetKey(next.videoUrl)
)

export const IncidentImages = ({ images }: IncidentImagesIProps) => {
  const { t } = useTranslation()
  const {
    index,
    isImageError,
    handleImageError,
    handleClickNext,
    handleClickPrevious,
  } = useMediaCounter({
    images,
  })
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const { isDownloading, handleDownload } = useFileDownload()
  const { getAttachmentMimeType, getAttachmentType, isReady } =
    useAttachmentTypes(images)

  const currentImageUrl = images?.[index]
  const currentAttachmentType = getAttachmentType(currentImageUrl)
  const isCurrentFile = currentAttachmentType === AttachmentType.File
  const isCurrentImage = currentAttachmentType === AttachmentType.Image
  const isCurrentPdf = currentAttachmentType === AttachmentType.Pdf
  const isCurrentVideo = currentAttachmentType === AttachmentType.Video
  // The original filename is not stored, so the position is the best label.
  // PDFs keep the extension because FileThumbnail derives its icon from it.
  const attachmentName = `Attachment ${index + 1}${isCurrentPdf ? '.pdf' : ''}`

  // Snapshot the signed URL when the previewer opens. Polling re-signs it every
  // three seconds, and a changed src remounts the <embed>, which flickers the
  // PDF. The feed previewer holds the file in state for the same reason.
  const handleOpenPreview = () => {
    setPreviewUrl(currentImageUrl)
    onOpen()
  }

  const handleClosePreview = () => {
    setPreviewUrl(null)
    onClose()
  }

  const selectedFile = previewUrl
    ? {
        id: previewUrl,
        url: previewUrl,
        name: getFileName(previewUrl),
        mimeType: getAttachmentMimeType(previewUrl) ?? getMimeType(previewUrl),
      }
    : null

  return (
    <Box
      data-testid='incidentsPage_drawer_incidentImages'
      pt='20px'
      px='24px'
      w='100%'
    >
      <MediaControls
        index={index}
        items={images}
        loading={!isReady}
        onClickNext={handleClickNext}
        onClickPrevious={handleClickPrevious}
      >
        {/* Attachments are no longer images only (ENG-30297) */}
        {isReady && isCurrentPdf && (
          <Flex alignItems='center' h='324px' justifyContent='center' w='100%'>
            <FileThumbnail name={attachmentName} onClick={handleOpenPreview} />
          </Flex>
        )}
        {isReady && isCurrentFile && (
          <Flex alignItems='center' h='324px' justifyContent='center' w='100%'>
            <FileThumbnail
              isDownloading={isDownloading}
              name={attachmentName}
              onDownload={() => handleDownload(currentImageUrl, attachmentName)}
            />
          </Flex>
        )}
        {isReady && isCurrentVideo && (
          <Flex
            borderRadius='4px'
            h='324px'
            overflow='hidden'
            position='relative'
            w='100%'
          >
            <IncidentAttachmentVideoPlayer videoUrl={currentImageUrl} />
            <IconButton
              aria-label={t('incidents:drawer_expand_video_aria')}
              color='gray.500'
              icon={<BsEyeFill />}
              onClick={handleOpenPreview}
              position='absolute'
              right='10px'
              size='sm'
              top='10px'
              zIndex='1'
            />
          </Flex>
        )}
        {isReady && isCurrentImage && !isImageError && (
          <Image
            borderRadius='4px'
            cursor='pointer'
            h='324px'
            objectFit='contain'
            onClick={handleOpenPreview}
            onError={handleImageError}
            src={currentImageUrl}
            w='100%'
          />
        )}
        {isReady && isCurrentImage && isImageError && <MediaError />}
      </MediaControls>
      {selectedFile && isOpen && (
        <MediaPreviewerModal
          file={selectedFile}
          isOpen={isOpen}
          onClose={handleClosePreview}
        />
      )}
    </Box>
  )
}
